/**
 * Grouped bar (grouped column) data transform + resolution helpers.
 */
import type {
  GroupedBarConfig,
  GroupedBarDimension,
  GroupedBarItemCustomization,
  GroupedBarCell,
} from "@store/slices/chart-config-slice";
import { interpolateColor } from "@utils/chart-utils";

export type { GroupedBarCell };

export interface GroupedBarDatum {
  id: string;
  label: string;
  sourceLabel?: string;
}

export interface GroupedBarSeriesDatum {
  id: string;
  label: string;
  sourceLabel?: string;
  // Raw per-category cells. Configs saved before the widening store plain
  // numbers; normalizeGroupedCell coerces both to a GroupedBarCell.
  data: Array<number | GroupedBarCell>;
}

/** Coerces a stored value (number, pre-widening) or cell into a GroupedBarCell. */
export function normalizeGroupedCell(c: number | GroupedBarCell): GroupedBarCell {
  return typeof c === "number" ? { value: c } : c;
}

/** A selectable item with the label variants used to match response rows. */
export interface GroupedReconcileItem {
  id: string;
  label: string;
  matchLabels: string[];
}

export interface GroupedBarTransformResult {
  categories: GroupedBarDatum[];
  series: GroupedBarSeriesDatum[];
}

/** Label that identifies which sub-series a descriptive metric belongs to. */
function getMetricSeriesLabel(metric: any): string {
  return (
    metric?.crossVariableLabel ||
    metric?.valueMetric ||
    metric?.timePeriodLabel ||
    ""
  );
}

/** Stable identity for a descriptive metric's sub-series. */
function getMetricSeriesId(metric: any): string {
  return String(metric?.crossVariableId || getMetricSeriesLabel(metric) || "");
}

/**
 * Transposes the API response into grouped-bar shape. Categories are the item
 * identities (grid rows for the current focus); series are the distinct
 * sub-metric identities, each carrying one value per category.
 */
export function processGroupedBarChartData(
  data: any[],
): GroupedBarTransformResult {
  const categories: GroupedBarDatum[] = data.map((item) => ({
    id: String(item.variableId ?? item.variableLabel ?? ""),
    label: item.variableLabel ?? "",
  }));

  // Collect distinct sub-series in order of first appearance, keyed by id.
  const seriesOrder: string[] = [];
  const seriesLabelById = new Map<string, string>();
  data.forEach((item) => {
    const metrics = item.surveys?.[0]?.descriptiveMetrics ?? [];
    metrics.forEach((metric: any) => {
      const id = getMetricSeriesId(metric);
      if (id && !seriesLabelById.has(id)) {
        seriesLabelById.set(id, getMetricSeriesLabel(metric));
        seriesOrder.push(id);
      }
    });
  });

  const series: GroupedBarSeriesDatum[] = seriesOrder.map((id) => ({
    id,
    label: seriesLabelById.get(id) ?? "",
    data: data.map((item): GroupedBarCell => {
      const metrics = item.surveys?.[0]?.descriptiveMetrics ?? [];
      const match = metrics.find((m: any) => getMetricSeriesId(m) === id);
      if (!match) return { value: 0 };
      return {
        value: match.value != null ? match.value : 0,
        statSignificance: match.statSignificance,
      };
    }),
  }));

  return { categories, series };
}

export function getGroupedVariableLabel(item: any): string {
  return item?.rowtitle || item?.coltitle || item?.title || item?.label || "";
}

export function getGroupedValueLabel(item: any): string {
  return item?.title || item?.label || "";
}

function buildLabelMatcher(
  items: GroupedReconcileItem[],
): Map<string, { id: string; label: string }> {
  const map = new Map<string, { id: string; label: string }>();
  items.forEach((item) => {
    item.matchLabels.forEach((ml) => {
      if (ml && !map.has(ml)) map.set(ml, { id: item.id, label: item.label });
    });
  });
  return map;
}

function reconcileItem<T extends GroupedBarDatum>(
  item: T,
  matcher: Map<string, { id: string; label: string }>,
): T {
  const key = item.sourceLabel ?? item.label;
  const hit = matcher.get(key);
  return {
    ...item,
    id: hit?.id ?? item.id,
    label: hit?.label ?? item.label,
    sourceLabel: key,
  };
}

/**
 * Rewrites chart-data ids to the selector datasetElementIds (and default labels
 * to the display labels) by matching the response machine label against each
 * selectable item's label variants. Idempotent — `sourceLabel` preserves the
 * original response label as the stable match key.
 */
export function reconcileGroupedChartData(
  data: { categories: GroupedBarDatum[]; series: GroupedBarSeriesDatum[] },
  variables: GroupedReconcileItem[],
  values: GroupedReconcileItem[],
  groupBy: GroupedBarDimension,
): { categories: GroupedBarDatum[]; series: GroupedBarSeriesDatum[] } {
  const groupMatcher = buildLabelMatcher(
    groupBy === "variable" ? variables : values,
  );
  const seriesMatcher = buildLabelMatcher(
    groupBy === "variable" ? values : variables,
  );
  return {
    categories: data.categories.map((c) => reconcileItem(c, groupMatcher)),
    series: data.series.map((s) => reconcileItem(s, seriesMatcher)),
  };
}

/** True when two chart-data structures have identical id/label/sourceLabel. */
export function groupedChartDataEquals(
  a: { categories: GroupedBarDatum[]; series: GroupedBarSeriesDatum[] },
  b: { categories: GroupedBarDatum[]; series: GroupedBarSeriesDatum[] },
): boolean {
  const sameList = (x: GroupedBarDatum[], y: GroupedBarDatum[]): boolean =>
    x.length === y.length &&
    x.every(
      (item, i) =>
        item.id === y[i].id &&
        item.label === y[i].label &&
        item.sourceLabel === y[i].sourceLabel,
    );
  return sameList(a.categories, b.categories) && sameList(a.series, b.series);
}

/**
 * Remaps freshly transformed (live) data to the reconciled ids/labels using a
 * previously stored, reconciled chartData — lets the dashboard filter path honor
 * selection without re-loading the question's variable universe.
 */
export function remapLiveGroupedData(
  base: GroupedBarTransformResult,
  stored:
    | { categories: GroupedBarDatum[]; series: GroupedBarSeriesDatum[] }
    | undefined,
): GroupedBarTransformResult {
  if (!stored) return base;
  const buildMap = (items: GroupedBarDatum[]) => {
    const m = new Map<string, { id: string; label: string }>();
    items.forEach((it) => {
      const key = it.sourceLabel ?? it.label;
      if (key && !m.has(key)) m.set(key, { id: it.id, label: it.label });
    });
    return m;
  };
  const catMap = buildMap(stored.categories);
  const serMap = buildMap(stored.series);
  const remap = <T extends GroupedBarDatum>(
    item: T,
    map: Map<string, { id: string; label: string }>,
  ): T => {
    const hit = map.get(item.label);
    return hit
      ? { ...item, id: hit.id, label: hit.label, sourceLabel: item.label }
      : item;
  };
  return {
    categories: base.categories.map((c) => remap(c, catMap)),
    series: base.series.map((s) => remap(s, serMap)),
  };
}

/** Resolves the effective (override-aware) label for a single item. */
function labelWithOverride(
  original: string,
  cust: GroupedBarItemCustomization | undefined,
): string {
  const override = cust?.labelOverride;
  return override && override.trim() !== "" ? override : original;
}

/** Sorts items by their position in `order`; unlisted items keep base order. */
function applyOrder<T extends { id: string }>(items: T[], order: string[]): T[] {
  if (order.length === 0) return items;
  const index = new Map(order.map((id, i) => [id, i]));
  return [...items].sort(
    (a, b) =>
      (index.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
      (index.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  );
}

// ─── Drawer: managed item lists (Groups / Series sections + selectors) ──────

export interface GroupedManagedItem {
  id: string;
  originalLabel: string;
  displayLabel: string;
  hasOverride: boolean;
  visible: boolean;
}

/**
 * Builds the ordered managed list from the chartData items (every group/series
 * always appears), resolving original label (from the item itself), any override
 * and visibility. Hiding marks an item; it is never removed from the list.
 */
export function resolveManagedItems(
  items: GroupedBarDatum[],
  order: string[],
  customizations: Record<string, GroupedBarItemCustomization>,
): GroupedManagedItem[] {
  const ordered = applyOrder(
    items.map((it) => ({ id: it.id, label: it.label })),
    order,
  );
  return ordered.map(({ id, label }) => {
    const cust = customizations[id];
    const override = cust?.labelOverride;
    const hasOverride = !!(override && override.trim() !== "");
    return {
      id,
      originalLabel: label,
      displayLabel: hasOverride ? override! : label,
      hasOverride,
      visible: cust?.visible ?? true,
    };
  });
}

// ─── Render resolution (chart + preview + dashboard) ────────────────────────

export interface ResolvedGroupedBarSeries {
  id: string;
  label: string;
  data: GroupedBarCell[];
}

export interface ResolvedGroupedBar {
  categories: string[];
  series: ResolvedGroupedBarSeries[];
}

function dimensionState(
  groupedBar: GroupedBarConfig,
  dimension: GroupedBarDimension,
) {
  const isVariable = dimension === "variable";
  return {
    order: isVariable ? groupedBar.variableOrder : groupedBar.valueOrder,
    customizations: isVariable
      ? groupedBar.variableCustomizations
      : groupedBar.valueCustomizations,
  };
}

/**
 * Counts the currently visible groups and series (from chartData). Used for the
 * summary line and the save validity gate. Groups are chartData.categories,
 * series are chartData.series (the response is already in Group By orientation).
 */
export function getGroupedVisibleCounts(
  groupedBar: GroupedBarConfig | undefined,
  groupBy: GroupedBarDimension,
): { groups: number; series: number } {
  const cd = groupedBar?.chartData;
  if (!groupedBar || !cd) return { groups: 0, series: 0 };
  const seriesDimension: GroupedBarDimension =
    groupBy === "variable" ? "value" : "variable";
  const groupCust = dimensionState(groupedBar, groupBy).customizations;
  const seriesCust = dimensionState(groupedBar, seriesDimension).customizations;
  return {
    groups: cd.categories.filter((c) => groupCust[c.id]?.visible ?? true).length,
    series: cd.series.filter((s) => seriesCust[s.id]?.visible ?? true).length,
  };
}

/**
 * Applies selection, visibility, custom order and label overrides to the raw
 * transform result, producing Highcharts-ready categories + series. The base
 * categories always correspond to the current group dimension and the base
 * series to the other dimension (the backend response shape follows Group By).
 */
export function resolveGroupedBarRender(
  base: GroupedBarTransformResult,
  groupedBar: GroupedBarConfig | undefined,
  groupBy: GroupedBarDimension,
): ResolvedGroupedBar {
  if (base.categories.length === 0) return { categories: [], series: [] };

  // Without grouped state (transient), render everything.
  if (!groupedBar) {
    return {
      categories: base.categories.map((c) => c.label),
      series: base.series.map((s) => ({
        id: s.id,
        label: s.label,
        data: s.data.map(normalizeGroupedCell),
      })),
    };
  }

  const seriesDimension: GroupedBarDimension =
    groupBy === "variable" ? "value" : "variable";
  const group = dimensionState(groupedBar, groupBy);
  const seriesDim = dimensionState(groupedBar, seriesDimension);

  // Groups (categories): keep visible, then order. Hidden items drop out here at
  // render time but remain in the drawer's Groups list.
  let cats = base.categories.map((c, i) => ({ ...c, sourceIndex: i }));
  cats = cats.filter((c) => group.customizations[c.id]?.visible ?? true);
  cats = applyOrder(cats, group.order);

  // Series: keep visible, then order.
  let series = [...base.series];
  series = series.filter((s) => seriesDim.customizations[s.id]?.visible ?? true);
  series = applyOrder(series, seriesDim.order);

  return {
    categories: cats.map((c) =>
      labelWithOverride(c.label, group.customizations[c.id]),
    ),
    series: series.map((s) => ({
      id: s.id,
      label: labelWithOverride(s.label, seriesDim.customizations[s.id]),
      data: cats.map((c) => normalizeGroupedCell(s.data[c.sourceIndex] ?? 0)),
    })),
  };
}

// ─── Series colors ──────────────────────────────────────────────────────────
// Palettes map to the series dimension: one color per stable series identity,
// reused in every group. Colors are resolved by the item's index in the saved
// series order so hiding/reordering never shifts another series' color.

export const GROUPED_DEFAULT_PALETTE = "Multicolor";
export const GROUPED_CUSTOM_PALETTE = "Custom (per-series)";

// Default multicolor sequence — starts with blue per the chosen default.
const GROUPED_MULTICOLOR_COLORS = [
  "#57bfe7", // blue
  "#57dfbe", // green
  "#ffb09c", // orange
  "#a1a7df", // purple
  "#c36179", // crimson
  "#57a68c", // dark green
  "#757679", // gray
  "#ebdd7c", // yellow
];

function paletteColorAt(
  palette: string,
  index: number,
  total: number,
  manualColors: Record<string, string>,
  id: string,
): string {
  if (palette === GROUPED_CUSTOM_PALETTE) {
    const manual = manualColors[id];
    if (manual) return manual;
    return GROUPED_MULTICOLOR_COLORS[index % GROUPED_MULTICOLOR_COLORS.length];
  }
  if (palette === "Gradient") {
    return interpolateColor(
      "#019EDB",
      "#00CF9C",
      total > 1 ? index / (total - 1) : 0,
    );
  }
  if (palette === "Grayscale") {
    return interpolateColor(
      "#f0f0f0",
      "#404040",
      total > 1 ? index / (total - 1) : 0,
    );
  }
  return GROUPED_MULTICOLOR_COLORS[index % GROUPED_MULTICOLOR_COLORS.length];
}

/**
 * Full series list (id + reconciled label) for the series dimension, taken from
 * the rendered chartData and put in saved order. Keying colors off the same ids
 * the chart draws with guarantees swatch ↔ color-map ↔ chart stay consistent,
 * independent of how the response ids reconcile to the selector ids.
 */
function orderedSeriesFromChartData(
  groupedBar: GroupedBarConfig,
  groupBy: GroupedBarDimension,
): Array<{ id: string; label: string }> {
  const chartData = groupedBar.chartData;
  if (!chartData) return [];
  const seriesDimension: GroupedBarDimension =
    groupBy === "variable" ? "value" : "variable";
  const { order } = dimensionState(groupedBar, seriesDimension);
  return applyOrder(
    chartData.series.map((s) => ({ id: s.id, label: s.label })),
    order,
  );
}

/**
 * Effective color per series id (every series in chartData, hidden included so
 * colors stay stable). Used by the chart at render time.
 */
export function resolveGroupedSeriesColors(
  groupedBar: GroupedBarConfig | undefined,
  groupBy: GroupedBarDimension,
): Map<string, string> {
  const map = new Map<string, string>();
  if (!groupedBar) return map;
  const series = orderedSeriesFromChartData(groupedBar, groupBy);
  const palette = groupedBar.colorPalette ?? GROUPED_DEFAULT_PALETTE;
  const manual = groupedBar.seriesManualColors ?? {};
  series.forEach(({ id }, i) =>
    map.set(id, paletteColorAt(palette, i, series.length, manual, id)),
  );
  return map;
}

export interface GroupedSeriesColorEntry {
  id: string;
  displayName: string;
  fallbackColor: string;
}

/**
 * Visible series entries (in saved order) for the Custom-palette swatch list:
 * display label + fallback palette color. Built from the rendered chartData so
 * ids match the chart exactly. Fallback index uses the full series order so a
 * swatch color doesn't shift when other series are hidden.
 */
export function resolveGroupedSeriesColorEntries(
  groupedBar: GroupedBarConfig | undefined,
  groupBy: GroupedBarDimension,
): GroupedSeriesColorEntry[] {
  if (!groupedBar) return [];
  const seriesDimension: GroupedBarDimension =
    groupBy === "variable" ? "value" : "variable";
  const { customizations } = dimensionState(groupedBar, seriesDimension);
  const series = orderedSeriesFromChartData(groupedBar, groupBy);
  return series
    .map(({ id, label }, i) => ({ id, label, i }))
    // Only visible series get a swatch (hiding removes it, matching the Series
    // section).
    .filter(({ id }) => customizations[id]?.visible ?? true)
    .map(({ id, label, i }) => {
      const override = customizations[id]?.labelOverride;
      const displayName =
        override && override.trim() !== "" ? override : label;
      return {
        id,
        displayName,
        fallbackColor:
          GROUPED_MULTICOLOR_COLORS[i % GROUPED_MULTICOLOR_COLORS.length],
      };
    });
}
